import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MultiSelect } from 'primereact/multiselect';
import { getDistricts, getNeighborhoods, getStreets } from '../services/addressService';

const NeighborhoodFilterModal = ({ isOpen, onClose, onFilterChange, currentFilters = {} }) => {
  const [selectedFilters, setSelectedFilters] = useState(currentFilters);
  const [districts, setDistricts] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [streets, setStreets] = useState([]);
  const [loading, setLoading] = useState({
    districts: false,
    neighborhoods: false,
    streets: false
  });

  useEffect(() => {
    if (isOpen) {
      fetchDistricts();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedFilters.district?.value) {
      fetchNeighborhoods(selectedFilters.district.value);
    }
  }, [selectedFilters.district]);

  useEffect(() => {
    if (selectedFilters.neighborhood?.value) {
      fetchStreets(selectedFilters.neighborhood.value);
    }
  }, [selectedFilters.neighborhood]);

  const fetchDistricts = async () => {
    try {
      setLoading(prev => ({ ...prev, districts: true }));
      const response = await getDistricts();
      if (response.status === 200) {
        setDistricts(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch districts:', error);
    } finally {
      setLoading(prev => ({ ...prev, districts: false }));
    }
  };

  const fetchNeighborhoods = async (districtId) => {
    try {
      setLoading(prev => ({ ...prev, neighborhoods: true }));
      const response = await getNeighborhoods(districtId);
      if (response.status === 200) {
        setNeighborhoods(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch neighborhoods:', error);
    } finally {
      setLoading(prev => ({ ...prev, neighborhoods: false }));
    }
  };

  const fetchStreets = async (neighborhoodId) => {
    try {
      setLoading(prev => ({ ...prev, streets: true }));
      const response = await getStreets(neighborhoodId);
      if (response.status === 200) {
        setStreets(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch streets:', error);
    } finally {
      setLoading(prev => ({ ...prev, streets: false }));
    }
  };

  const handleInputChange = (key, value) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      const newFilters = { ...selectedFilters };
      delete newFilters[key];
      setSelectedFilters(newFilters);
    } else {
      setSelectedFilters(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onFilterChange(selectedFilters);
    onClose();
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Geniş Filtreleme</h3>
        </div>
        <form onSubmit={handleSubmit} className="filter-form">
          <div className="form-group">
            <label>Tarih</label>
            <input
              type="date"
              value={selectedFilters.date || ''}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="form-group">
            <label>İlçe</label>
            <select
              value={selectedFilters.district?.value || ''}
              onChange={(e) => {
                const selectedDistrict = districts.find(d => d.id === e.target.value);
                handleInputChange('district', selectedDistrict ? {
                  value: selectedDistrict.id,
                  label: selectedDistrict.ad
                } : null);
              }}
              className="filter-input"
              disabled={loading.districts}
            >
              <option value="">İlçe Seçiniz</option>
              {districts.map(district => (
                <option key={district.id} value={district.id}>
                  {district.ad}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Mahalle</label>
            <select
              value={selectedFilters.neighborhood?.value || ''}
              onChange={(e) => {
                const selectedNeighborhood = neighborhoods.find(n => n.id === e.target.value);
                handleInputChange('neighborhood', selectedNeighborhood ? {
                  value: selectedNeighborhood.id,
                  label: selectedNeighborhood.ad
                } : null);
              }}
              className="filter-input"
              disabled={loading.neighborhoods || !selectedFilters.district}
            >
              <option value="">Mahalle Seçiniz</option>
              {neighborhoods.map(neighborhood => (
                <option key={neighborhood.id} value={neighborhood.id}>
                  {neighborhood.ad}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Sokak</label>
            <MultiSelect
              value={selectedFilters.street || []}
              options={streets.map(street => ({
                label: street.ad,
                value: street.ad
              }))}
              onChange={(e) => handleInputChange('street', e.value)}
              optionLabel="label"
              placeholder="Sokak Seçiniz"
              className="filter-input"
              disabled={loading.streets || !selectedFilters.neighborhood}
              filter
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="neighborhood-filter-submit-button">
              Filtrele
            </button>
          </div>
        </form>
      </div>
      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          padding: 20px;
          border-radius: 8px;
          width: 500px;
          max-width: 90%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .form-group {
          margin-bottom: 15px;
        }

        label {
          display: block;
          margin-bottom: 5px;
          font-weight: bold;
        }

        .filter-input {
          width: 100%;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
        }

        .form-actions {
          margin-top: 20px;
          text-align: right;
        }

        .neighborhood-filter-submit-button {
          background-color: rgba(24, 150, 78, 0.2) !important;
          color: rgba(24, 150, 78, 1) !important;
          padding: 10px 20px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          width: 100%;
        }
      `}</style>
    </div>
  );
};

NeighborhoodFilterModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  currentFilters: PropTypes.object
};

export default NeighborhoodFilterModal; 